import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { logError } from "@/utils/error";

import { getCurrentUsername, getLibraryId } from "../api/auth";
import { AuthState, UserDef } from "../types/auth.types";

const initialState: AuthState = {
  loginSuccess: false,
  user: null,
  libraryId: 0,
  openSetting: false,
  isLoading: false,
  error: null,
};

export const getLibraryIdThunk = createAsyncThunk<number>(
  "auth/getLibraryId",
  async (_, { rejectWithValue }) => {
    try {
      const result = await getLibraryId();
      return result.data;
    } catch (error) {
      logError("getLibraryIdThunk", error);
      return rejectWithValue(error);
    }
  }
);

export const getProfileThunk = createAsyncThunk<UserDef>(
  "auth/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const result = await getCurrentUsername();
      return result;
    } catch (error) {
      logError("getProfileThunk", error);
      return rejectWithValue(error);
    }
  }
);

export const checkAuthThunk = createAsyncThunk<{
  user: UserDef;
  libraryId?: number;
} | null>("auth/checkAuth", async (_, { rejectWithValue }) => {
  try {
    const user = await getCurrentUsername();
    if (!user) {
      return null;
    }

    const libraryResult = await getLibraryId();
    return {
      user,
      libraryId: libraryResult?.data || undefined,
    };
  } catch (error) {
    logError("checkAuthThunk", error);
    return rejectWithValue(error);
  }
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      state.loginSuccess = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setUserData: (state, action: PayloadAction<UserDef | null>) => {
      if (action.payload == null) {
        state.loginSuccess = false;
        state.libraryId = 0;
      }
      state.user = action.payload;
    },
    hiddenSetting: (state) => {
      state.openSetting = false;
    },
    showSetting: (state) => {
      state.openSetting = true;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      if (action.payload) {
        state.isLoading = false;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Library ID thunk
    builder
      .addCase(getLibraryIdThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getLibraryIdThunk.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.isLoading = false;
          state.libraryId = action.payload;
          state.error = null;
        }
      )
      .addCase(getLibraryIdThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || "Không thể lấy thông tin thư viện";
      });

    // Profile thunk
    builder
      .addCase(getProfileThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getProfileThunk.fulfilled,
        (state, action: PayloadAction<UserDef>) => {
          state.isLoading = false;
          state.user = action.payload;
          state.error = null;
        }
      )
      .addCase(getProfileThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || "Không thể lấy thông tin người dùng";
      });

    // Check Auth thunk
    builder
      .addCase(checkAuthThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        checkAuthThunk.fulfilled,
        (
          state,
          action: PayloadAction<{
            user: UserDef;
            libraryId?: number | undefined;
          } | null>
        ) => {
          state.isLoading = false;
          if (action.payload) {
            state.loginSuccess = true;
            state.user = action.payload.user;
            state.libraryId = action.payload.libraryId || 0;
          } else {
            state.loginSuccess = false;
            state.user = null;
            state.libraryId = 0;
          }
          state.error = null;
        }
      )
      .addCase(checkAuthThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.loginSuccess = false;
        state.user = null;
        state.libraryId = 0;
        state.error =
          action.error.message || "Không thể kiểm tra trạng thái đăng nhập";
      });
  },
});

export const authAction = authSlice.actions;

export default authSlice.reducer;
